import React, { useState, useEffect, useRef } from 'react';
import '../styles/DraggableTimer.css';

interface DraggableTimerProps {
    onClose: () => void;
}

const DraggableTimer: React.FC<DraggableTimerProps> = ({ onClose }) => {
    const [position, setPosition] = useState({ x: window.innerWidth / 2 - 150, y: window.innerHeight / 2 - 100 });
    const [size, setSize] = useState({ width: 300, height: 200 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Timer state
    const [timeLeft, setTimeLeft] = useState(0); // in seconds
    const [isActive, setIsActive] = useState(false);
    const [inputMinutes, setInputMinutes] = useState('05');
    const [inputSeconds, setInputSeconds] = useState('00');
    const [isEditing, setIsEditing] = useState(true);

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);

    // Audio Logic
    const getAudioContext = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        return audioContextRef.current;
    };

    const playNote = (ctx: AudioContext, freq: number, time: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(time);
        gain.gain.setValueAtTime(0.1, time);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration);
        osc.stop(time + duration);
    };

    const playCountdownNote = (seconds: number) => {
        const ctx = getAudioContext();
        if (!ctx) return;
        const now = ctx.currentTime;

        // Descending notes for 3, 2, 1
        const freqs: { [key: number]: number } = {
            3: 523.25, // C5
            2: 493.88, // B4
            1: 440.00  // A4
        };

        if (freqs[seconds]) {
            playNote(ctx, freqs[seconds], now, 0.3);
        }
    };

    const playJingle = () => {
        const ctx = getAudioContext();
        if (!ctx) return;
        const now = ctx.currentTime;

        const playSequence = (startTime: number) => {
            playNote(ctx, 523.25, startTime, 0.2);       // C5
            playNote(ctx, 659.25, startTime + 0.2, 0.2); // E5
            playNote(ctx, 783.99, startTime + 0.4, 0.2); // G5
            playNote(ctx, 1046.50, startTime + 0.6, 0.4); // C6
        };

        // Play 5 times
        for (let i = 0; i < 5; i++) {
            playSequence(now + (i * 1.5));
        }
    };

    // Timer Logic
    useEffect(() => {
        if (isActive && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft((prev) => {
                    const next = prev - 1;

                    // Play countdown notes
                    if (next <= 3 && next > 0) {
                        playCountdownNote(next);
                    }

                    if (next <= 0) {
                        playJingle();
                        setIsActive(false);
                        return 0;
                    }
                    return next;
                });
            }, 1000);
        } else if (timeLeft === 0) {
            setIsActive(false);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isActive, timeLeft]);

    const handleStart = () => {
        if (isEditing) {
            const mins = parseInt(inputMinutes) || 0;
            const secs = parseInt(inputSeconds) || 0;
            setTimeLeft(mins * 60 + secs);
            setIsEditing(false);
        }
        setIsActive(true);
    };

    const handlePause = () => {
        setIsActive(false);
    };

    const handleReset = () => {
        setIsActive(false);
        setIsEditing(true);
        setTimeLeft(0);
    };

    // Dragging Logic
    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.timer-controls') || (e.target as HTMLElement).closest('.timer-close')) return;
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    // Resizing Logic
    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsResizing(true);
        setDragOffset({
            x: e.clientX - size.width,
            y: e.clientY - size.height
        });
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragOffset.x,
                    y: e.clientY - dragOffset.y
                });
            }
            if (isResizing) {
                setSize({
                    width: Math.max(250, e.clientX - dragOffset.x), // Min width 250
                    height: Math.max(180, e.clientY - dragOffset.y) // Min height 180
                });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, dragOffset]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className="draggable-timer"
            style={{
                left: position.x,
                top: position.y,
                width: size.width,
                height: size.height,
            }}
            onMouseDown={handleMouseDown}
        >
            <div className="timer-header">
                <span className="timer-title">⏱️ Timer</span>
                <button className="timer-close" onClick={onClose}>×</button>
            </div>

            <div
                className="timer-content"
                style={{ fontSize: Math.min(size.width / 10, size.height / 5) }}
            >
                {isEditing ? (
                    <div className="timer-input-container">
                        <input
                            type="number"
                            className="timer-input"
                            value={inputMinutes}
                            onChange={(e) => setInputMinutes(e.target.value)}
                            placeholder="00"
                        />
                        <span className="timer-separator">:</span>
                        <input
                            type="number"
                            className="timer-input"
                            value={inputSeconds}
                            onChange={(e) => setInputSeconds(e.target.value)}
                            placeholder="00"
                        />
                    </div>
                ) : (
                    <div className="timer-display" onClick={() => !isActive && setIsEditing(true)}>
                        {formatTime(timeLeft)}
                    </div>
                )}

                <div className="timer-controls">
                    {!isActive ? (
                        <button className="timer-btn start" onClick={handleStart}>▶️ Start</button>
                    ) : (
                        <button className="timer-btn pause" onClick={handlePause}>⏸️ Pause</button>
                    )}
                    <button className="timer-btn reset" onClick={handleReset}>↺ Reset</button>
                </div>
            </div>

            <div className="resize-handle" onMouseDown={handleResizeMouseDown} />
        </div>
    );
};

export default DraggableTimer;
